import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { ArrowRight } from "components/icons/ArrowRight";
import { ClassificationRadioGroup } from "components/Transaction/Filter/ClassificationFilter";
import { AddNewClass } from "components/Transaction/Slider/Classifications";
import {
  ACCOUNTING_METHOD_LABELS,
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS_OPTIONS,
  INCOME_STATEMENT,
  monthPeriod,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import {
  COMPARE_BY,
  COMPARE_BY_OPTIONS,
  CompareByType,
  OTHER_OPTIONS,
  VARIANCE_OPTIONS,
  VarianceType,
  VIEW_BY_OPTIONS,
} from "constants/reports";
import { OtherItemType, PERIOD, PeriodType } from "constants/revenueMetrics";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  UpdateFilterFunction,
  UpdateMultipleFilterFunction,
} from "hooks/useFilter";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { useModal } from "hooks/useModal";
import { ReportsType } from "pages/Books/Reports/Reports";
import { ComponentProps, useEffect, useState } from "react";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { ClassificationGroup } from "types/Models/classification";
import { formatDateByViewType } from "utils/formatDate";
import { getDatePeriod } from "utils/getDatePeriod";
import {
  DateRangeValue,
  getDateRange,
  getDateRangeWithSetting,
} from "utils/getDateRange";

dayjs.extend(quarterOfYear);

export type ReportFilterType = {
  START_DATE: string;
  END_DATE: string;
  ACCOUNTING_METHOD: ACCOUNTING_METHOD_TYPES;
  VIEW_BY: PeriodType | null;
  HIDE_ZERO: boolean;
  TXN_GROUP_ID: string | null;
  CLASS_IDS: string[];
  CLASSIFICATION: string[];
  NO_CLASSIFICATION: string[];
  OTHER: OtherItemType | null;
  COMPARE_BY: CompareByType;
  VARIANCE: VarianceType[];
  PERCENTAGE_MODE: string | null;
  FORECAST_ENABLED: boolean;
  FORECAST_START: string;
  FORECAST_END: string;
  FORECAST_SET_ID: string | null;
};

const dateFilterMap: {
  [P in PeriodType]: ComponentProps<typeof BooksDateFilter>["pickerType"];
} = {
  TOTALS: "date",
  MONTHLY: "month",
  QUARTERLY: "quarter",
  YEARLY: "year",
};

const AccountingMethod = ({
  filter,
  updateFilter,
  reportType,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  reportType: ReportsType;
}) => {
  return (
    <Radio.Root
      defaultValue={filter.ACCOUNTING_METHOD}
      onValueChange={(v: ACCOUNTING_METHOD_TYPES) =>
        updateFilter("ACCOUNTING_METHOD", v)
      }
    >
      <Radio.Content className="t-gap-3 t-flex t-flex-col">
        {ACCOUNTING_METHODS_OPTIONS.map((item) => (
          <Radio.Item key={item.value} value={item.value}>
            {item.label}
          </Radio.Item>
        ))}
      </Radio.Content>
    </Radio.Root>
  );
};

const ClassificationChips = ({
  filter,
  classificationData,
  updateFilter,
}: {
  filter: ReportFilterType;
  classificationData: ClassificationGroup[];
  updateFilter: UpdateFilterFunction<ReportFilterType>;
}) => {
  const handleRemoveClassification = (uuid: string) => {
    const item = classificationData.find((c) => c.uuid === uuid);
    if (!item) return;
    const updatedClassifications = filter.CLASSIFICATION.filter(
      (id) => id !== uuid
    );
    const updatedClassIds = filter.CLASS_IDS.filter(
      (id) => !item.classes.some((cls) => cls.uuid === id)
    );
    updateFilter("CLASSIFICATION", updatedClassifications);
    updateFilter("CLASS_IDS", updatedClassIds);
  };

  const handleRemoveNoClassification = (uuid: string) => {
    updateFilter(
      "NO_CLASSIFICATION",
      filter.NO_CLASSIFICATION.filter((id) => id !== uuid)
    );
  };

  return (
    <>
      {filter.CLASSIFICATION.map((uuid) => {
        const item = classificationData.find((c) => c.uuid === uuid);
        if (!item || item.name === "Classes") return null;
        const selectedClassCount = item.classes.filter((cls) =>
          filter.CLASS_IDS.includes(cls.uuid)
        ).length;
        if (selectedClassCount === 0) return null;
        return (
          <Chip
            key={uuid}
            size="xsmall"
            onRemove={() => handleRemoveClassification(uuid)}
            isActive
            filterType="CLASSIFICATIONS"
          >
            {item.name} ({selectedClassCount})
          </Chip>
        );
      })}
      {filter.NO_CLASSIFICATION.map((uuid) => {
        const group = classificationData.find((c) => c.uuid === uuid);
        if (!group) return null;
        return (
          <Chip
            key={uuid + "-noClassification"}
            size="xsmall"
            onRemove={() => handleRemoveNoClassification(uuid)}
            isActive
            filterType="CLASSIFICATIONS"
          >
            {group.name} (1)
          </Chip>
        );
      })}
    </>
  );
};

const Classifications = ({
  filter,
  updateFilter,
  reportType,
  entityId,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  reportType: ReportsType;
  entityId: string;
}) => {
  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId || reportType !== INCOME_STATEMENT,
    }
  );
  return (
    <ClassificationRadioGroup
      classifications={filter.CLASSIFICATION}
      classes={filter.CLASS_IDS}
      noClassification={filter.NO_CLASSIFICATION}
      classificationData={classification}
      onValueChange={(payload) => {
        updateClassificationFilters(updateFilter, payload);
      }}
    />
  );
};
function updateClassificationFilters(
  updateFilter: UpdateFilterFunction<ReportFilterType>,
  payload: {
    classification?: string[];
    classes?: string[];
    noClassification?: string[];
  }
) {
  updateFilter("CLASSIFICATION", payload.classification || []);
  updateFilter("CLASS_IDS", payload.classes || []);
  updateFilter("NO_CLASSIFICATION", payload.noClassification || []);
}

const Others = ({
  filter,
  updateFilter,
  isGroupByView,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  isGroupByView: boolean;
}) => {
  return (
    <Checkbox
      name="hideZero"
      label="Hide zero balances"
      onChange={(e) => {
        updateFilter("HIDE_ZERO", e.target.checked);
      }}
      checked={filter.HIDE_ZERO}
    />
  );
};

const ViewBy = ({
  filter,
  updateFilter,
  onMultipleFilterUpdate,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  onMultipleFilterUpdate?: UpdateMultipleFilterFunction<ReportFilterType>;
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroup();
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const datePeriod = getDatePeriod;

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId! || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const onDateChange: ComponentProps<typeof BooksDateFilter>["updateFilter"] = (
    name,
    value
  ) => {
    if (!value) return;

    if (name === "SELECT_PERIOD" && value !== "CUSTOM") {
      const { startDate, endDate } = getDateRangeWithSetting(
        value as DateRangeValue,
        bookkeepingSettings?.tax_year_end,
        bookkeepingStartDate
      );
      const startDateObj = startDate.format(YYYY_MM_DD);
      const endDateObj = endDate.format(YYYY_MM_DD);

      if (onMultipleFilterUpdate) {
        onMultipleFilterUpdate({
          START_DATE: startDateObj,
          END_DATE: endDateObj,
          [name]: value,
        });
        return;
      }

      updateFilter("START_DATE", startDateObj);
      updateFilter("END_DATE", endDateObj);
    }
    updateFilter(name as keyof ReportFilterType, value);
  };

  const type = dateFilterMap[filter.VIEW_BY || PERIOD.TOTALS] || "date";

  const values = {
    SELECT_PERIOD: "",
    START_DATE: filter.START_DATE ? dayjs(filter.START_DATE).toString() : "",
    END_DATE: filter.END_DATE ? dayjs(filter.END_DATE).toString() : "",
  };

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full">
      {type === "date" && (
        <BooksDateFilter
          datePeriod={datePeriod}
          skipDateUpdateOnPeriodChange
          pickerType="date"
          values={values}
          updateFilter={onDateChange}
        />
      )}
      {type === "year" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="year"
          values={values}
          updateFilter={onDateChange}
          showPeriodSelector={false}
        />
      )}
      {type === "quarter" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="quarter"
          values={values}
          updateFilter={onDateChange}
          showPeriodSelector={false}
        />
      )}
      {type === "month" && (
        <BooksDateFilter
          skipDateUpdateOnPeriodChange
          pickerType="month"
          values={values}
          updateFilter={onDateChange}
          datePeriod={monthPeriod}
        />
      )}
    </div>
  );
};

const CompareFinancialsContent = ({
  filter,
  updateFilter,
  isMoreThanAYear,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  isMoreThanAYear: boolean;
}) => {
  const onCompareByChange = (value: CompareByType) => {
    updateFilter("COMPARE_BY", value);
    updateFilter("TXN_GROUP_ID", null);
    updateFilter("VIEW_BY", null);
    updateFilter("CLASS_IDS", []);
    updateFilter("CLASSIFICATION", []);
    updateFilter("NO_CLASSIFICATION", []);
    updateFilter("OTHER", null);
    updateFilter("FORECAST_ENABLED", false);
  };

  const onVarianceChange = (value: VarianceType, checked: boolean) => {
    const currentVariance = filter.VARIANCE;
    if (checked) {
      updateFilter("VARIANCE", [...currentVariance, value]);
    } else {
      updateFilter(
        "VARIANCE",
        currentVariance.filter((v) => v !== value)
      );
    }
  };

  const isVarianceDisabled = !filter.COMPARE_BY;

  const isPreviousYearDisabled = isMoreThanAYear;

  return (
    <div className="t-flex t-flex-col t-gap-1">
      <div className="t-flex t-flex-col t-gap-2">
        <Radio.Root
          value={filter.COMPARE_BY || ""}
          onValueChange={(v) => onCompareByChange(v as CompareByType)}
        >
          <Radio.Content className="t-gap-2 t-flex t-flex-col ">
            {COMPARE_BY_OPTIONS.map((item) => {
              const isDisabled =
                item.value === COMPARE_BY.PREVIOUS_YEAR &&
                isPreviousYearDisabled;

              return (
                <ConditionalToolTip
                  key={item.value}
                  condition={
                    isDisabled && (
                      <div className="t-w-52 t-flex t-text-start">
                        Past Year comparison is only available when the current
                        period is 1 year or less
                      </div>
                    )
                  }
                  align="start"
                >
                  <div className="t-p-1">
                    <Radio.Item value={item.value} disabled={isDisabled}>
                      {item.label}
                    </Radio.Item>
                  </div>
                </ConditionalToolTip>
              );
            })}
          </Radio.Content>
        </Radio.Root>
        <Dropdown.Separator />
      </div>

      <div className="t-flex t-flex-col t-gap-2">
        <Dropdown.Label className="t-group t-w-full t-truncate !t-border-none !t-p-1">
          Variance
        </Dropdown.Label>
        {VARIANCE_OPTIONS.map((item) => (
          <ConditionalToolTip
            key={item.value}
            condition={
              isVarianceDisabled && (
                <div className="t-w-52 t-flex t-text-start">
                  Only applicable when comparison period is selected above
                </div>
              )
            }
            align="start"
          >
            <div className="t-p-1">
              <Checkbox
                name={item.value}
                label={item.label}
                checked={filter.VARIANCE.includes(item.value)}
                disabled={isVarianceDisabled}
                onChange={(e) => onVarianceChange(item.value, e.target.checked)}
              />
            </div>
          </ConditionalToolTip>
        ))}
      </div>
    </div>
  );
};

const GroupSubtriggerContent = ({
  filter,
  updateFilter,
  entityId,
  reportType,
  close,
  openAddClass,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  entityId: string;
  reportType: ReportsType;
  close: () => void;
  openAddClass: (groupName: string) => void;
}) => {
  const { data: classification = [], ...classificationQueryState } =
    useGetAllClassificationQuery(
      { entityId },
      {
        skip: !entityId || reportType !== INCOME_STATEMENT,
      }
    );

  const onChange = (value: PeriodType) => {
    let updatedStartDate = dayjs(filter.START_DATE);
    let updatedEndDate = dayjs(filter.END_DATE);

    if (value === PERIOD.MONTHLY) {
      updatedStartDate = updatedStartDate.startOf("month");
      updatedEndDate = updatedEndDate.endOf("month");
    }

    if (value === PERIOD.QUARTERLY) {
      updatedStartDate = updatedStartDate.startOf("quarter");
      updatedEndDate = updatedEndDate.endOf("quarter");
    }

    if (value === PERIOD.YEARLY) {
      updatedStartDate = updatedStartDate.startOf("year");
      updatedEndDate = updatedEndDate.endOf("year");
    }

    if (updatedEndDate.isAfter(dayjs())) {
      updatedEndDate = dayjs();
    }

    const startDate = updatedStartDate.format(YYYY_MM_DD);
    const endDate = updatedEndDate.format(YYYY_MM_DD);

    updateFilter("VIEW_BY", value);
    updateFilter("START_DATE", startDate);
    updateFilter("END_DATE", endDate);
    updateFilter("TXN_GROUP_ID", null);
    updateFilter("CLASS_IDS", []);
    updateFilter("OTHER", null);
    updateFilter("COMPARE_BY", null);
    updateFilter("VARIANCE", []);
  };

  const onGroupSelect = (groupId: string) => {
    updateFilter("TXN_GROUP_ID", groupId);
    updateFilter("VIEW_BY", null);
    updateFilter("CLASS_IDS", []);
    updateFilter("CLASSIFICATION", []);
    updateFilter("NO_CLASSIFICATION", []);
    updateFilter("OTHER", null);
    updateFilter("COMPARE_BY", null);
    updateFilter("VARIANCE", []);
    updateFilter("FORECAST_ENABLED", false);
  };

  const onOtherSelect = (value: OtherItemType) => {
    updateFilter("OTHER", value);
    updateFilter("TXN_GROUP_ID", null);
    updateFilter("VIEW_BY", null);
    updateFilter("CLASS_IDS", []);
    updateFilter("CLASSIFICATION", []);
    updateFilter("NO_CLASSIFICATION", []);
    updateFilter("COMPARE_BY", null);
    updateFilter("VARIANCE", []);
    updateFilter("FORECAST_ENABLED", false);
  };

  useEffect(() => {
    const selectedGroup = classification.find(
      (group) => group.uuid === filter.TXN_GROUP_ID
    );
    // Reset group and classes if entity or group is changed
    if (!selectedGroup && classificationQueryState.isSuccess) {
      updateFilter("TXN_GROUP_ID", null);
      updateFilter("CLASS_IDS", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.TXN_GROUP_ID, classification, classificationQueryState.isSuccess]);

  return (
    <>
      {reportType === INCOME_STATEMENT && (
        <>
          <Dropdown.Label>Classification Groups</Dropdown.Label>
          <Async.Root
            isEmpty={classification.length === 0}
            isLoading={classificationQueryState.isLoading}
            isSuccess={classificationQueryState.isSuccess}
            renderOnLoad={
              <div className="t-flex t-flex-col t-gap-2">
                <Skeleton className="t-h-9 t-w-full" />
                <Skeleton className="t-h-9 t-w-full" />
              </div>
            }
          >
            <Async.Empty>
              <Dropdown.Item disabled>No groups created yet.</Dropdown.Item>
            </Async.Empty>
            <Async.Success>
              {classification.map((item) => (
                <ConditionalToolTip
                  key={item.uuid}
                  condition={
                    item.classes.length === 0 && (
                      <>
                        No classes added <br />
                        for this group yet{" "}
                        <Button
                          onClick={() => {
                            close();
                            openAddClass(item.name);
                          }}
                          customType="link"
                          size="small"
                        >
                          <span className="t-text-text t-underline">
                            Add class
                          </span>
                        </Button>
                      </>
                    )
                  }
                  side="right"
                >
                  <Dropdown.Item
                    key={item.uuid}
                    className={classNames({
                      "t-bg-purple-0": filter.TXN_GROUP_ID === item.uuid,
                    })}
                    disabled={item.classes.length === 0}
                    onSelect={() => onGroupSelect(item.uuid)}
                  >
                    {item.name}
                  </Dropdown.Item>
                </ConditionalToolTip>
              ))}
            </Async.Success>
          </Async.Root>
          <Dropdown.Separator />
        </>
      )}

      <Dropdown.Label>Period</Dropdown.Label>
      {VIEW_BY_OPTIONS.filter(({ value }) => value !== PERIOD.TOTALS).map(
        ({ label, value }) => (
          <Dropdown.Item
            key={value}
            onSelect={() => onChange(value)}
            className={classNames({
              "t-bg-purple-0": filter.VIEW_BY === value,
            })}
          >
            {label}
          </Dropdown.Item>
        )
      )}

      {reportType === INCOME_STATEMENT && (
        <>
          <Dropdown.Separator />
          <Dropdown.Label>Other</Dropdown.Label>
          {OTHER_OPTIONS.map(({ label, value }) => (
            <Dropdown.Item
              key={value}
              onSelect={() => onOtherSelect(value)}
              className={classNames({
                "t-bg-purple-0": filter.OTHER === value,
              })}
            >
              {label}
            </Dropdown.Item>
          ))}
        </>
      )}
    </>
  );
};

const ViewByFilter = ({
  filter,
  updateFilter,
  entityId,
  reportType,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  entityId: string;
  reportType: ReportsType;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useWindowSize();
  const addNewClassModal = useModal();
  const [addClassToGroup, setAddClassToGroup] = useState<string | null>(null);

  const isMoreThanAYear =
    dayjs(filter.END_DATE).diff(dayjs(filter.START_DATE), "year", true) > 1;

  useEffect(() => {
    if (isMoreThanAYear && filter.COMPARE_BY === COMPARE_BY.PREVIOUS_YEAR) {
      updateFilter("COMPARE_BY", null);
      updateFilter("VARIANCE", []);
    }
  }, [isMoreThanAYear, filter.COMPARE_BY, updateFilter]);

  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId || reportType !== INCOME_STATEMENT,
    }
  );

  const selectedView = VIEW_BY_OPTIONS.find(
    (option) => option.value === filter.VIEW_BY
  );

  const periodTitle =
    selectedView?.value === PERIOD.TOTALS ? null : selectedView?.label;

  const selectedGroup = classification.find(
    (group) => group.uuid === filter.TXN_GROUP_ID
  );

  const groupTitle = selectedGroup ? selectedGroup.name : null;

  const selectedOther = OTHER_OPTIONS.find(
    (item) => item.value === filter.OTHER
  );

  const otherTitle = selectedOther ? selectedOther.label : null;

  const selectedCompareBy = COMPARE_BY_OPTIONS.find(
    (item) => item.value === filter.COMPARE_BY
  );

  const compareByTitle = selectedCompareBy ? selectedCompareBy.label : null;

  let title: string | null = null;
  let prefix = "View by";

  if (filter.TXN_GROUP_ID && groupTitle) {
    title = groupTitle;
    prefix = "Group by:";
  } else if (filter.OTHER && otherTitle) {
    title = otherTitle;
    prefix = "Group by:";
  } else if (periodTitle) {
    title = periodTitle;
    prefix = "Group by:";
  } else if (filter.COMPARE_BY && compareByTitle) {
    title = compareByTitle;
    prefix = "Compare by:";
  } else {
    title = "None";
  }

  const hasSelection =
    filter.TXN_GROUP_ID ||
    filter.OTHER ||
    filter.VIEW_BY ||
    filter.COMPARE_BY ||
    (filter.VARIANCE?.length ?? 0) > 0;

  const onReset = () => {
    updateFilter("VIEW_BY", null);
    updateFilter("TXN_GROUP_ID", null);
    updateFilter("CLASS_IDS", []);
    updateFilter("CLASSIFICATION", []);
    updateFilter("NO_CLASSIFICATION", []);
    updateFilter("OTHER", null);
    updateFilter("COMPARE_BY", null);
    updateFilter("VARIANCE", []);
    updateFilter("FORECAST_ENABLED", false);
  };

  return (
    <>
      <Dropdown.Root modal={false} open={isOpen} onOpenChange={setIsOpen}>
        <Dropdown.Trigger className="all:unset t-group t-self-center">
          <Button size="xsmall">
            <span className="t-text-text-30">{prefix}</span>
            {title && <span className="t-ml-1 t-text-text-60"> {title}</span>}
            <div className="t-text-text-30 t-transition-transform t-duration-200 t-rotate-90 group-data-[state=open]:-t-rotate-90">
              <ArrowRight color="currentColor" size="22" stroke="1.25" />
            </div>
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={8}
            alignOffset={isMobile ? -24 : 0}
            className="!t-min-w-40 t-max-h-96 t-overflow-y-auto"
            align="start"
          >
            <>
              <Dropdown.Sub>
                <Dropdown.SubTrigger>Group</Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent
                    sideOffset={isMobile ? 2 : 8}
                    className="!t-min-w-52 t-max-h-96 t-overflow-y-auto sm:!t-min-w-44"
                  >
                    <GroupSubtriggerContent
                      filter={filter}
                      updateFilter={updateFilter}
                      entityId={entityId}
                      reportType={reportType}
                      close={() => setIsOpen(false)}
                      openAddClass={(groupName) => {
                        setAddClassToGroup(groupName);
                        addNewClassModal.open();
                      }}
                    />
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
              <Dropdown.Sub>
                <Dropdown.SubTrigger>Compare financials</Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent
                    sideOffset={isMobile ? 2 : 8}
                    className="!t-min-w-48 t-p-2.5 sm:!t-min-w-44"
                  >
                    <CompareFinancialsContent
                      filter={filter}
                      updateFilter={updateFilter}
                      isMoreThanAYear={isMoreThanAYear}
                    />
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
              {hasSelection && (
                <>
                  <Dropdown.Separator />
                  <Dropdown.Item onSelect={onReset}>
                    <span className="t-text-purple">Reset</span>
                  </Dropdown.Item>
                </>
              )}
            </>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <AddNewClass
        {...addNewClassModal}
        entityId={entityId}
        addClassToGroup={addClassToGroup}
        onSuccess={() => {
          addNewClassModal.close();
          setAddClassToGroup(null);
        }}
      />
    </>
  );
};

const ClassFilter = ({
  filter,
  updateFilter,
  selectedGroup,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  selectedGroup: ClassificationGroup;
}) => {
  return (
    <MultiSelectFilter
      isSelectAll={false}
      onChange={(values) => updateFilter("CLASS_IDS", values)}
      checkboxSpace="small"
      options={selectedGroup.classes.map((cls) => ({
        label: cls.name,
        value: cls.uuid,
      }))}
      selected={filter.CLASS_IDS}
    />
  );
};

type CalculationOption = {
  label: string;
  value: string;
  tooltip?: string;
};

const CalculationsFilter = ({
  filter,
  updateFilter,
  calculationOptions,
}: {
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  calculationOptions: CalculationOption[];
}) => {
  const selectedOption = calculationOptions.find(
    (opt) => opt.value === filter.PERCENTAGE_MODE
  );

  const onReset = () => {
    updateFilter("PERCENTAGE_MODE", null);
  };

  const title = selectedOption ? selectedOption.label : "Calculations";
  const { isMobile } = useWindowSize();

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger className="all:unset t-group t-self-center">
        <Button size="xsmall">
          <span className="t-text-text-30">{title}</span>
          <div className="t-text-text-30 t-transition-transform t-duration-200 t-rotate-90 group-data-[state=open]:-t-rotate-90">
            <ArrowRight color="currentColor" size="22" stroke="1.25" />
          </div>
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content
          sideOffset={8}
          className="!t-min-w-48 t-max-h-96 t-overflow-y-auto sm:t-max-w-72"
          align="start"
          collisionPadding={12}
        >
          <Dropdown.Label>Calculations</Dropdown.Label>
          {calculationOptions.map((opt) => (
            <ConditionalToolTip
              key={opt.value}
              condition={
                !isMobile && (
                  <div className="t-w-60 t-text-left">{opt.tooltip}</div>
                )
              }
              side="right"
              sideOffset={4}
            >
              <span>
                <Dropdown.Item
                  onSelect={() => updateFilter("PERCENTAGE_MODE", opt.value)}
                  className={classNames({
                    "t-bg-purple-0": filter.PERCENTAGE_MODE === opt.value,
                  })}
                >
                  <div className="t-flex t-flex-col t-gap-0.5">
                    {opt.label}
                    {isMobile && opt.tooltip && (
                      <span className="t-whitespace-normal t-text-caption t-text-text-30">
                        {opt.tooltip}
                      </span>
                    )}
                  </div>
                </Dropdown.Item>
              </span>
            </ConditionalToolTip>
          ))}
          {filter.PERCENTAGE_MODE && (
            <>
              <Dropdown.Separator />
              <Dropdown.Item onSelect={onReset}>
                <span className="t-text-purple">Reset</span>
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

export const ReportFilter = ({
  reportType,
  filter,
  updateFilter,
  onMultipleFilterUpdate,
  entityId,
  isGroupByView,
  showClassificationInFilters = true,
  hideViewBy = false,
  hideForecast = true,
}: {
  reportType: ReportsType;
  filter: ReportFilterType;
  updateFilter: UpdateFilterFunction<ReportFilterType>;
  onMultipleFilterUpdate?: UpdateMultipleFilterFunction<ReportFilterType>;
  entityId: string;
  isGroupByView: boolean;
  showClassificationInFilters?: boolean;
  hideViewBy?: boolean;
  hideForecast?: boolean;
}) => {
  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId || reportType !== INCOME_STATEMENT,
    }
  );

  const selectedGroup = classification.find(
    (group) => group.uuid === filter.TXN_GROUP_ID
  );

  const toggleForecast = () => {
    const enabling = !filter.FORECAST_ENABLED;
    const isPeriodView =
      filter.VIEW_BY === "MONTHLY" ||
      filter.VIEW_BY === "QUARTERLY" ||
      filter.VIEW_BY === "YEARLY";
    if (enabling && !isPeriodView) {
      updateFilter("VIEW_BY", "MONTHLY");
      updateFilter("TXN_GROUP_ID", null);
      updateFilter("CLASS_IDS", []);
      updateFilter("CLASSIFICATION", []);
      updateFilter("NO_CLASSIFICATION", []);
      updateFilter("OTHER", null);
      updateFilter("COMPARE_BY", null);
      updateFilter("VARIANCE", []);
    }
    updateFilter("FORECAST_ENABLED", enabling);
  };

  const showClassificationFilter = filter.TXN_GROUP_ID === null;

  const appliedFilterCount = Object.keys(filter).reduce((acc, key) => {
    if (
      key === "VIEW_BY" ||
      key === "TXN_GROUP_ID" ||
      key === "CLASSIFICATION" ||
      key === "COMPARE_BY" ||
      key === "VARIANCE"
    ) {
      return acc;
    }

    if (key === "CLASS_IDS") {
      return acc + filter.CLASS_IDS.length;
    }

    if (key === "NO_CLASSIFICATION") {
      return acc + filter.NO_CLASSIFICATION.length;
    }

    if (filter[key as keyof ReportFilterType]) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const selectedView = VIEW_BY_OPTIONS.find(
    (option) => option.value === filter.VIEW_BY
  )?.label;

  const permission = useHasSubscriptionPermission({
    feature: CLASSIFICATION_FEATURE_ACCESS,
  });
  const { data: classificationData = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );
  const { isMobile } = useWindowSize();

  return (
    <div className="t-flex t-gap-2 t-items-start t-flex-wrap sm:t-contents">
      {!hideViewBy && (
        <div className="t-flex t-gap-2 t-items-start sm:t-order-1">
          <ViewByFilter
            entityId={entityId}
            filter={filter}
            updateFilter={updateFilter}
            reportType={reportType}
          />
          <div className="t-border-0 t-border-solid t-border-neutral-10 t-h-6 t-border-l sm:t-hidden" />
        </div>
      )}
      <div className="t-contents">
        {!hideForecast && (
          <>
            <Button
              size="xsmall"
              className="sm:t-order-6"
              onClick={toggleForecast}
              leftIcon={
                <Switch
                  size="small"
                  checked={filter.FORECAST_ENABLED}
                  onCheckedChange={() => {}}
                />
              }
            >
              <span className="t-text-caption t-text-text-60">Forecast</span>
            </Button>
            <div className="t-border-0 t-border-solid t-border-neutral-10 t-h-6 t-border-l sm:t-hidden" />
          </>
        )}
        <div
          className={classNames({
            "t-flex t-gap-2 t-items-start sm:t-order-2": isMobile,
            "t-contents": !isMobile,
          })}
        >
          <Filter.Root
            defaultValue="VIEW_BY"
            hideDivider={isMobile}
            withWrapper={isMobile}
            title={
              <span className="t-text-text-30">
                {appliedFilterCount ? <>({appliedFilterCount})</> : null}
              </span>
            }
            capsule={
              <span className="t-contents sm:t-hidden">
                {filter.CLASS_IDS.length > 0 &&
                  filter.CLASSIFICATION.length < 0 &&
                  filter.NO_CLASSIFICATION.length < 0 && (
                    <Chip
                      size="xsmall"
                      onRemove={() => updateFilter("CLASS_IDS", [])}
                      isActive
                      filterType="GROUP"
                    >
                      Classes ({filter.CLASS_IDS.length})
                    </Chip>
                  )}
                <Chip
                  size="xsmall"
                  onRemove={() => {}}
                  isRemovable={false}
                  isActive
                  filterType="VIEW_BY"
                >
                  {formatDateByViewType(
                    filter.START_DATE,
                    filter.VIEW_BY || PERIOD.TOTALS
                  )}
                  {" - "}
                  {formatDateByViewType(
                    filter.END_DATE,
                    filter.VIEW_BY || PERIOD.TOTALS
                  )}
                </Chip>
                <Chip
                  size="xsmall"
                  onRemove={() => {}}
                  isRemovable={false}
                  isActive
                  filterType="ACCOUNTING_METHOD"
                >
                  {ACCOUNTING_METHOD_LABELS[filter.ACCOUNTING_METHOD]}
                </Chip>
                {filter.HIDE_ZERO && (
                  <Chip
                    size="xsmall"
                    onRemove={() => updateFilter("HIDE_ZERO", false)}
                    isActive
                    filterType="OTHERS"
                  >
                    Hide zero balances
                  </Chip>
                )}
                <ClassificationChips
                  filter={filter}
                  classificationData={classificationData}
                  updateFilter={updateFilter}
                />
              </span>
            }
          >
            <Filter.Portal>
              <Filter.List>
                {selectedGroup && (
                  <Filter.ListItem value="GROUP">
                    {selectedGroup.name}
                  </Filter.ListItem>
                )}
                <Filter.ListItem value="VIEW_BY">
                  {selectedView || "Date"} range
                </Filter.ListItem>
                <Filter.ListItem value="ACCOUNTING_METHOD">
                  Accounting method
                </Filter.ListItem>
                {!permission?.blocked &&
                  reportType === INCOME_STATEMENT &&
                  showClassificationFilter &&
                  showClassificationInFilters && (
                    <Filter.ListItem value="CLASSIFICATIONS">
                      Classifications
                    </Filter.ListItem>
                  )}
                <Filter.ListItem value="OTHERS">Others</Filter.ListItem>
              </Filter.List>
              <Filter.Body value="GROUP" block>
                {selectedGroup && (
                  <ClassFilter
                    filter={filter}
                    updateFilter={updateFilter}
                    selectedGroup={selectedGroup}
                  />
                )}
              </Filter.Body>
              <Filter.Body value="VIEW_BY" block>
                <ViewBy
                  filter={filter}
                  updateFilter={updateFilter}
                  onMultipleFilterUpdate={onMultipleFilterUpdate}
                />
              </Filter.Body>
              <Filter.Body value="ACCOUNTING_METHOD" block>
                <AccountingMethod
                  reportType={reportType}
                  filter={filter}
                  updateFilter={updateFilter}
                />
              </Filter.Body>
              {!permission?.blocked &&
                reportType === INCOME_STATEMENT &&
                showClassificationFilter &&
                showClassificationInFilters && (
                  <Filter.Body value="CLASSIFICATIONS" block>
                    <Classifications
                      reportType={reportType}
                      filter={filter}
                      updateFilter={updateFilter}
                      entityId={entityId}
                    />
                  </Filter.Body>
                )}
              <Filter.Body value="OTHERS" block>
                <Others
                  filter={filter}
                  updateFilter={updateFilter}
                  isGroupByView={isGroupByView}
                />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
        </div>
      </div>
    </div>
  );
};
